import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Clapperboard, GalleryHorizontal, Image, Loader2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { cn } from '@orisini/ui/lib/utils'
import { contentApi, type Kind } from '../../content/lib/content'
import type { Workspace } from '../lib/accounts'

const KINDS: { value: Kind; title: string; hint: string; icon: typeof Image }[] = [
  { value: 'poster', title: 'Poster', hint: 'One image', icon: Image },
  { value: 'carousel', title: 'Carousel', hint: 'Several slides', icon: GalleryHorizontal },
  { value: 'video', title: 'Video', hint: 'Narrated video', icon: Clapperboard },
]

/** Judul konten diambil dari baris pertama prompt, dipotong di batas kata. */
function judulDari(prompt: string): string {
  const baris = prompt.trim().split('\n')[0]!.trim()
  if (baris.length <= 80) return baris
  const potong = baris.slice(0, 80)
  return `${potong.slice(0, potong.lastIndexOf(' ') > 40 ? potong.lastIndexOf(' ') : 80)}…`
}

/**
 * Create di dalam project: satu jenis konten + satu prompt -> satu konten.
 * Memakai brief yang sama dengan /create, jadi hasilnya mengikuti Workflow
 * project ini: draft menunggu di Review (atau langsung diproduksi kalau mode
 * auto), lalu ke tujuan yang diatur di sana.
 */
export function CreateTab({ workspace: w }: { workspace: Workspace }) {
  const navigate = useNavigate()
  const [kind, setKind] = useState<Kind>('poster')
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)

  const kirim = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!prompt.trim()) return
    setBusy(true)
    try {
      await contentApi.brief({
        urls: [],
        title: judulDari(prompt),
        text: prompt.trim(),
        notes: '',
        imageAssetId: null,
        workspaceIds: [w.id],
        kinds: [kind],
        destination: null,
        ...(kind === 'video' ? { duration: { mode: 'auto' } } : {}),
      })
      toast.success('Writing the draft. It appears in Review as soon as it is ready.')
      navigate(`/bank?status=working&account=${w.id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't start the draft")
      setBusy(false)
    }
  }

  return (
    <form onSubmit={kirim} className="max-w-2xl space-y-5">
      <Card className="space-y-4 p-5">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Content type</span>
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Content type">
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                role="radio"
                aria-checked={kind === k.value}
                onClick={() => setKind(k.value)}
                className={cn(
                  'flex items-center gap-3 rounded-md border px-3 py-2.5 text-left',
                  kind === k.value ? 'border-primary bg-primary/10' : 'border-border hover:border-foreground/40',
                )}
              >
                <k.icon className="size-5 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm font-medium">{k.title}</span>
                  <span className="block text-xs text-muted-foreground">{k.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-prompt">Prompt</Label>
          <Textarea
            id="c-prompt"
            rows={6}
            maxLength={12000}
            value={prompt}
            placeholder={
              kind === 'carousel'
                ? 'Five common beginner mistakes, one per slide, with the fix for each.'
                : kind === 'video'
                  ? 'Explain the idea in under a minute: what it is, why it matters, and one example.'
                  : 'A quote poster: “Every expert was once a beginner.”'
            }
            onChange={(e) => setPrompt(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            What the {kind} should say. Orisini writes a draft in this project&apos;s style, then follows this project&apos;s Workflow before production.
          </p>
        </div>
      </Card>

      <Button type="submit" disabled={busy || !prompt.trim()}>
        {busy && <Loader2 className="animate-spin" />}
        Create draft
      </Button>
    </form>
  )
}
